import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  LinearProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CleaningServicesRoundedIcon from "@mui/icons-material/CleaningServicesRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import DateRangePresetButtons from "../../components/DateRangePresetButtons";
import { getDateRangePreset } from "../../utils/dateRangePresets";
import { formatEpoch } from "../../utils/dateUtils";
import {
  getPersonal,
  getTransactionsByDays,
  getTransactionsByFilter,
  getTransactionsByNode,
  getTransactionsByPersonal,
  getTransactionsByProduct,
} from "../../api/digitalPayAPI";
import { useSnackbar } from "../../hooks/useSnackbar";
import {
  BALANCE_TYPES,
  DP,
  balanceTypeLabel,
  formatCardNo,
  formatKurus,
} from "./constants";
import { Panel, ListGrid, PrimaryButton, GhostButton } from "./ui";
import ReportChart from "./ReportChart";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";

const VIEW_OPTIONS = [
  { value: "day", label: "Güne Göre" },
  { value: "machine", label: "Otomata Göre" },
  { value: "product", label: "Ürüne Göre" },
  { value: "personal", label: "Personele Göre" },
  { value: "all", label: "Tüm Satışlar" },
];

const REPORT_LOADERS = {
  day: getTransactionsByDays,
  machine: getTransactionsByNode,
  product: getTransactionsByProduct,
  personal: getTransactionsByPersonal,
  all: getTransactionsByFilter,
};

const VIEW_FIELD_ORDER = {
  day: ["label", "salesCount", "totalAmount", "epoch", "date", "day"],
  machine: ["label", "salesCount", "totalAmount", "nodeName", "nid", "nodeId"],
  product: ["label", "salesCount", "totalAmount", "productNo", "productName"],
  personal: [
    "label",
    "salesCount",
    "totalAmount",
    "personalId",
    "nameSurname",
    "cardNo",
    "departmentName",
  ],
  all: [
    "epoch",
    "personalId",
    "nameSurname",
    "cardNo",
    "departmentName",
    "nodeName",
    "nid",
    "productNo",
    "productName",
    "amountType",
    "previousBalance",
    "amount",
    "postBalance",
    "transactionTypeId",
    "transactionId",
  ],
};

const HEADER_NAMES = {
  epoch: "Tarih",
  date: "Tarih",
  day: "Gün",
  transactionId: "İşlem No",
  nid: "Otomat No",
  nodeId: "Otomat No",
  idnode: "Otomat No",
  nodeName: "Otomat Adı",
  machineName: "Otomat Adı",
  vendingName: "Otomat Adı",
  productNo: "Slot No",
  productName: "Ürün",
  personalId: "Personel No",
  nameSurname: "Personel",
  cardNo: "Kart No",
  departmentName: "Departman",
  amountType: "Otomat Tipi",
  ballanceTypeId: "Otomat Tipi",
  transactionTypeId: "İşlem Tipi",
  saleCount: "Satış Adedi",
  salesCount: "Satış Adedi",
  count: "Adet",
  amount: "Satış Tutarı",
  totalAmount: "Toplam Satış",
  previousBalance: "Önceki Bakiye",
  postBalance: "Kalan Bakiye",
};

const MACHINE_ID_FIELDS = ["nid", "nodeId", "idnode", "machineId"];
const MACHINE_NAME_FIELDS = ["nodeName", "machineName", "vendingName"];
const HIDDEN_REPORT_FIELDS = new Set([
  "firmId",
  "firmName",
  "createdBy",
  "CreatedBy",
  "createdby",
  "idtransaction",
  "createTime",
  "craeteTime",
  "createdAt",
  "updateTime",
  "updatedAt",
]);

const withDayBounds = ({ start, end }) => ({
  start: `${start}T00:00:00`,
  end: `${end}T23:59:59`,
});

const createDefaultFilters = () => {
  const { start, end } = withDayBounds(getDateRangePreset("today"));
  return {
    start,
    end,
    view: "day",
    machineType: "",
    departmentName: "",
    personalId: "",
  };
};

const toEpochSeconds = (value) => {
  const milliseconds = new Date(value).getTime();
  return Number.isFinite(milliseconds) ? Math.floor(milliseconds / 1000) : 0;
};

const buildReportRequest = (filters) => {
  const request = {
    startEpoch: toEpochSeconds(filters.start),
    endEpoch: toEpochSeconds(filters.end),
    count: 0,
  };
  const personalId = Number(filters.personalId);
  const ballanceTypeId = Number(filters.machineType);

  // Filtre gövdesi: startEpoch · endEpoch · personalId · cardNo · departmentName
  // · transactionTypeId · ballanceTypeId · count. Boş bırakılanlar gönderilmez.
  // Otomat (nid) bu DTO'da yok — o filtre istemcide uygulanıyor.
  if (personalId > 0) request.personalId = personalId;
  if (filters.departmentName) request.departmentName = filters.departmentName;
  if (ballanceTypeId > 0) request.ballanceTypeId = ballanceTypeId;

  return request;
};

const normalizeRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row, index) => ({
    ...(row && typeof row === "object" ? row : { value: row }),
    __reportId:
      row?.idtransaction ??
      row?.idTransaction ??
      row?.id ??
      `${index}-${row?.epoch ?? row?.date ?? "row"}`,
  }));

const humanizeField = (field) =>
  String(field)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toLocaleUpperCase("tr-TR"));

const formatReportValue = (value, field, cardNoHex) => {
  if (value === null || value === undefined) return "";
  const normalizedField = field.toLocaleLowerCase("en-US");

  if (normalizedField === "cardno") return formatCardNo(value, cardNoHex);
  if (normalizedField.includes("epoch")) return formatEpoch(value);
  if (
    normalizedField === "amounttype" ||
    normalizedField === "ballancetypeid" ||
    normalizedField === "balancetypeid"
  ) {
    return balanceTypeLabel(value);
  }
  if (
    normalizedField.includes("amount") ||
    normalizedField.includes("balance") ||
    normalizedField.includes("price")
  ) {
    return formatKurus(value);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

export default function ReportsPanel({ lookups }) {
  const { departments = [], cardNoHex = false } = lookups || {};
  const [filters, setFilters] = useState(createDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartHidden, setChartHidden] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const loadReport = useCallback(
    async (nextFilters) => {
      const startEpoch = toEpochSeconds(nextFilters.start);
      const endEpoch = toEpochSeconds(nextFilters.end);
      if (!startEpoch || !endEpoch) {
        showSnackbar("Başlangıç ve bitiş tarihi zorunlu", "error");
        return;
      }
      if (startEpoch > endEpoch) {
        showSnackbar("Başlangıç tarihi bitiş tarihinden sonra olamaz", "error");
        return;
      }

      setLoading(true);
      const request = buildReportRequest(nextFilters);
      try {
        const loader = REPORT_LOADERS[nextFilters.view] || getTransactionsByFilter;
        const result = await loader(request);
        const normalizedRows = normalizeRows(result);
        setRows(normalizedRows);
        setAppliedFilters({ ...nextFilters });
      } catch (error) {
        setRows([]);
        showSnackbar(error.message || "Rapor verileri alınamadı", "error");
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  useEffect(() => {
    const initialFilters = createDefaultFilters();
    loadReport(initialFilters);
    getPersonal()
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [loadReport]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handlePreset = (key) => {
    const { start, end } = withDayBounds(getDateRangePreset(key));
    setSelectedPreset(key);
    setFilters((current) => ({ ...current, start, end }));
  };

  const departmentById = useMemo(
    () => new Map(departments.map((department) => [Number(department.iddepartment), department])),
    [departments]
  );

  const personOptions = useMemo(
    () =>
      people.filter((person) => {
        if (!filters.departmentName) return true;
        return (
          departmentById.get(Number(person.department_iddepartment))?.name ===
          filters.departmentName
        );
      }),
    [departmentById, filters.departmentName, people]
  );

  const columns = useMemo(() => {
    const keys = new Set();
    const hasMachineName = rows.some((row) =>
      MACHINE_NAME_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(row, field))
    );
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key === "__reportId") return;
        if (HIDDEN_REPORT_FIELDS.has(key)) return;
        if (hasMachineName && MACHINE_ID_FIELDS.includes(key)) return;
        keys.add(key);
      });
    });

    const preferred = VIEW_FIELD_ORDER[appliedFilters.view] || [];
    const labelHeader =
      appliedFilters.view === "day"
        ? "Gün"
        : appliedFilters.view === "machine"
          ? "Otomat Adı"
          : appliedFilters.view === "product"
            ? "Ürün"
            : appliedFilters.view === "personal"
              ? "Personel"
              : "Etiket";
    const orderedKeys = Array.from(keys).sort((a, b) => {
      if (a === "transactionId") return 1;
      if (b === "transactionId") return -1;
      const aIndex = preferred.indexOf(a);
      const bIndex = preferred.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b, "tr");
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return orderedKeys.map((field) => ({
      field,
      headerName:
        field === "label"
          ? labelHeader
          : HEADER_NAMES[field] || humanizeField(field),
      minWidth: 125,
      flex: 1,
      valueFormatter: (value) => formatReportValue(value, field, cardNoHex),
    }));
  }, [appliedFilters.view, rows, cardNoHex]);

  // Gün/Ürün/Personel görünümlerinde grid üstüne grafik: tutar (bar) + adet (line).
  const chartData = useMemo(() => {
    const pickLabel = (row) =>
      row.label ??
      row.day ??
      row.date ??
      row.productName ??
      row.nameSurname ??
      [row.name, row.surname].filter(Boolean).join(" ") ??
      "";
    const labels = [];
    const amounts = [];
    const counts = [];
    rows.forEach((row) => {
      labels.push(String(pickLabel(row)));
      // Tutar kuruş -> ₺
      amounts.push(Number(row.totalAmount ?? row.amount ?? 0) / 100);
      counts.push(Number(row.salesCount ?? row.saleCount ?? row.count ?? 0));
    });
    return { labels, amounts, counts };
  }, [rows]);

  const CHART_TITLES = {
    day: "Günlük Satış",
    product: "Ürün Bazında Satış",
    personal: "Personel Bazında Satış",
  };
  // Tek veri noktalı grafik anlamsız (bugün / pazartesi olan hafta / ayın ilk günü
  // hepsi tek bara düşer) -> en az 2 nokta iste.
  const chartAvailable =
    appliedFilters.view !== "all" && chartData.labels.length > 1;

  const clearFilters = () => {
    const cleared = createDefaultFilters();
    setFilters(cleared);
    setSelectedPreset("today");
    loadReport(cleared);
  };

  const handleExport = () => {
    const result = createAndDownloadExcel(
      rows,
      columns,
      "Satış Raporu",
      generateExcelFilename("Vendai_Digital_Pay_Satis_Raporu"),
      {
        valueTransformer: (value, column) =>
          formatReportValue(value, column.field, cardNoHex),
      }
    );
    if (result.success) showSnackbar(`İndirildi: ${result.fileName}`, "success");
    else showSnackbar(result.error || "Dışa aktarılamadı", "error");
  };

  const fieldSx = {
    minWidth: { xs: "100%", sm: 170 },
    "& .MuiOutlinedInput-root": { bgcolor: DP.surface },
  };

  return (
    <>
      <Panel>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) auto" },
            alignItems: "center",
            gap: 2,
            pb: 2.25,
            mb: 2.25,
            borderBottom: `1px solid ${DP.border}`,
          }}
        >
          <DateRangePresetButtons
            onSelect={handlePreset}
            selectedKey={selectedPreset}
            justifyContent="flex-start"
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", xl: "flex-end" },
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Başlangıç Tarihi"
              type="datetime-local"
              value={filters.start}
              onChange={(event) => {
                setSelectedPreset("");
                updateFilter("start", event.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1 }}
              size="small"
              sx={fieldSx}
            />
            <TextField
              label="Bitiş Tarihi"
              type="datetime-local"
              value={filters.end}
              onChange={(event) => {
                setSelectedPreset("");
                updateFilter("end", event.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1 }}
              size="small"
              sx={fieldSx}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              xl: "auto minmax(420px, 1fr) auto",
            },
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              select
              label="Görünüm"
              value={filters.view}
              onChange={(event) => updateFilter("view", event.target.value)}
              size="small"
              sx={fieldSx}
            >
              {VIEW_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", xl: "center" },
            }}
          >
            <TextField
              select
              label="Otomat Tipi"
              value={filters.machineType}
              onChange={(event) => updateFilter("machineType", event.target.value)}
              size="small"
              sx={fieldSx}
            >
              <MenuItem value="">Tümü</MenuItem>
              {BALANCE_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Departman"
              value={filters.departmentName}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  departmentName: event.target.value,
                  personalId: "",
                }))
              }
              size="small"
              sx={fieldSx}
            >
              <MenuItem value="">Tüm Departmanlar</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department.iddepartment} value={department.name}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Personel"
              value={filters.personalId}
              onChange={(event) => updateFilter("personalId", event.target.value)}
              size="small"
              sx={fieldSx}
            >
              <MenuItem value="">Tüm Personeller</MenuItem>
              {personOptions.map((person) => (
                <MenuItem key={person.idpersonal} value={person.idpersonal}>
                  {[person.name, person.surname].filter(Boolean).join(" ") || person.regNumber}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", xl: "flex-end" },
            }}
          >
            <PrimaryButton
              startIcon={<FilterAltRoundedIcon />}
              onClick={() => loadReport(filters)}
              disabled={loading}
            >
              Uygula
            </PrimaryButton>
            <GhostButton
              startIcon={<CleaningServicesRoundedIcon />}
              onClick={clearFilters}
              disabled={loading}
            >
              Temizle
            </GhostButton>
            <GhostButton
              startIcon={<FileDownloadRoundedIcon />}
              onClick={handleExport}
              disabled={loading || !rows.length}
            >
              Dışa Aktar
            </GhostButton>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mt: 2.5, borderRadius: 999 }} />}

        {chartAvailable && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <GhostButton
                startIcon={
                  chartHidden ? <BarChartRoundedIcon /> : <VisibilityOffRoundedIcon />
                }
                onClick={() => setChartHidden((hidden) => !hidden)}
              >
                {chartHidden ? "Grafiği Göster" : "Grafiği Gizle"}
              </GhostButton>
            </Box>
            {!chartHidden && (
              <ReportChart
                title={CHART_TITLES[appliedFilters.view]}
                labels={chartData.labels}
                amounts={chartData.amounts}
                counts={chartData.counts}
              />
            )}
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography sx={{ color: DP.textMuted, fontSize: "0.8rem", mb: 1.2 }}>
            {rows.length} sonuç
          </Typography>
          <ListGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.__reportId}
            loading={loading}
            height={520}
          />
        </Box>
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
